import type { VariableRefNode } from "../../types.js";
import {
  evaluateVariableOverride,
  isVariableEvalBlocked,
  VARIABLE_REF_OUT_PORT_ID,
  VARIABLE_VALUE_PORT_ID,
} from "../../variable-graph.js";
import { ensureBindingExpression } from "../helpers.js";
import { ExecutionContext } from "../types.js";
import { BehaviorNode } from "./BehaviorNode.js";

/**
 * Resolves a scene variable by evaluating its body graph and reading
 * the value wired into `variableEnd`.
 * If a runtime override exists (set via `setVariable`), evaluates
 * the stored subgraph instead of the catalog graph.
 */
export class VariableRefBehaviorNode extends BehaviorNode {
  declare behavior: VariableRefNode;

  constructor(behavior: VariableRefNode) {
    super(behavior);
  }

  execute(): never {
    throw new Error("variableRef nodes are evaluated via getOutput only.");
  }

  getOutput(
    portId: string,
    visiting: Set<string>,
    context: ExecutionContext,
    binding?: boolean,
  ): unknown {
    if (portId !== VARIABLE_REF_OUT_PORT_ID) {
      return undefined;
    }

    const { variableId } = this.behavior;
    if (isVariableEvalBlocked(context.variableEvalStack, variableId)) {
      return undefined;
    }

    const childContext: ExecutionContext = {
      ...context,
      variableEvalStack: [
        ...(context.variableEvalStack ?? []),
        { variableId, refNodeId: this.behavior.id },
      ],
    };

    const override = context.variableOverrideApi?.get(variableId);
    if (override) {
      const raw = evaluateVariableOverride(
        override,
        childContext,
        binding === true,
      );
      return binding === true ? ensureBindingExpression(raw) : raw;
    }

    const entry = context.variableRuntimeEntries?.get(variableId);
    if (!entry) {
      return undefined;
    }

    const inputs: Record<string, unknown> = {};
    for (const port of this.behavior.ports) {
      if (port.direction === "input") {
        inputs[port.id] = this.getInput(
          port.id,
          visiting,
          context,
          binding ?? false,
        );
      }
    }

    childContext.variableInputFrameStack = [
      ...(context.variableInputFrameStack ?? []),
      { inputs },
    ];

    const endNode = [...entry.runtime.nodes.values()].find(
      (node) =>
        node.behavior.type === "control" &&
        node.behavior.controlType === "variableEnd",
    );
    if (!endNode) {
      return undefined;
    }

    const childVisiting = new Set(visiting);
    const raw = endNode.getInput(
      VARIABLE_VALUE_PORT_ID,
      childVisiting,
      childContext,
      binding === true,
    );
    return binding === true ? ensureBindingExpression(raw) : raw;
  }
}
